import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent, SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { taskOutputSchema } from "./shared";

/**
 * Create Task Step
 *
 * Creates a new task with optional entity linking.
 * Used as both an agent tool (via ToolCatalog) and a workflow step (via StepRegistry).
 */
export const createTaskStep = defineStepConfig({
  type: "create_task",
  name: "Create Task",
  description: "Create a new task with optional entity linking",
  category: "Data Access",

  inputSchema: z.object({
    title: z.string().describe("Title of the task"),
    description: z.string().optional().describe("Detailed task description"),
    priority: z
      .enum(["low", "medium", "high", "urgent"])
      .optional()
      .describe("Task priority (defaults to medium)"),
    assigneeType: z
      .enum(["user", "agent"])
      .optional()
      .describe("Type of assignee"),
    assigneeUserId: z
      .uuid()
      .optional()
      .describe("User ID when assigneeType is user"),
    assigneeAgentDefinitionId: z
      .uuid()
      .optional()
      .describe("Agent definition ID when assigneeType is agent"),
    dueDate: z.iso
      .datetime()
      .optional()
      .describe("Due date as ISO datetime string"),
    metadata: z
      .record(z.string(), z.unknown())
      .optional()
      .describe("Arbitrary metadata to attach to the task"),
    tags: z.array(z.string()).optional().describe("Tags for the task"),
    watcherEmails: z
      .array(z.string().email())
      .optional()
      .describe(
        "Email addresses whose matching users should watch the task. Unknown addresses are ignored.",
      ),
    watcherUserIds: z
      .array(z.uuid())
      .optional()
      .describe(
        "User ids to add as task watchers. Prefer this over watcherEmails when the upstream step already resolved users.",
      ),
    taskNamespaceId: z
      .uuid()
      .optional()
      .describe(
        "Task List (namespace) to create the task in. Wire from an upstream step; overrides the config default. Omit to use the org default list.",
      ),
    sourceEntity: SystemEntitySchema.optional().describe(
      "Entity to link to this task via TASK_EXECUTION association",
    ),
    sourceEntityType: z
      .nativeEnum(SystemEntity)
      .optional()
      .describe(
        "Entity type to link to this task — an alternative to `sourceEntity` for when you only have a bare entity id (paired with `sourceEntityId`). Ignored if `sourceEntity` is provided.",
      ),
    sourceEntityId: z
      .uuid()
      .optional()
      .describe(
        "Entity id to link to this task — an alternative to `sourceEntity` for when you only have a bare entity id (paired with `sourceEntityType`). Ignored if `sourceEntity` is provided.",
      ),
  }),

  outputSchema: z.object({
    task: taskOutputSchema,
    created: z
      .boolean()
      .describe(
        "True when a new task was created, false when an existing task was returned via idempotency match",
      ),
  }),

  configSchema: z
    .object({
      assigneeType: z
        .enum(["user", "agent"])
        .optional()
        .describe("Default assignee type when input assigneeType is not set"),
      assigneeUserId: z
        .uuid()
        .optional()
        .describe("Default user assignee ID when assigneeType is user"),
      assigneeAgentDefinitionId: z
        .uuid()
        .optional()
        .describe(
          "Default agent definition assignee ID when assigneeType is agent",
        ),
      taskNamespaceKey: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe(
          "Task List key (e.g. 'SHIP') to create the task in. Portable across orgs — resolved to the namespace at run time. Used when the `taskNamespaceId` input is not wired.",
        ),
      taskNamespaceId: z
        .uuid()
        .optional()
        .describe(
          "Task List (namespace) id to create the task in. Prefer `taskNamespaceKey` in templates; the input socket takes precedence over both.",
        ),
    })
    .superRefine((value, ctx) => {
      if (value.assigneeType === "user" && !value.assigneeUserId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeUserId"],
          message: "assigneeUserId is required when assigneeType is 'user'",
        });
      }
      if (value.assigneeType === "agent" && !value.assigneeAgentDefinitionId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeAgentDefinitionId"],
          message:
            "assigneeAgentDefinitionId is required when assigneeType is 'agent'",
        });
      }
      if (
        !value.assigneeType &&
        (value.assigneeUserId || value.assigneeAgentDefinitionId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assigneeType"],
          message:
            "assigneeType is required when a default assignee ID is provided",
        });
      }
    }),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  emits: [SystemEvent.TASK_CREATED] as const,
});
